import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import baseURL from '../utils/baseURL';
import useAxiosAuth from '../hooks/useAxiosAuth';

// DOCTOR CALENDAR EVENTS — surgery / meeting / other.
//
// Not appointments. They belong to the doctor, carry no patient, and exist so
// that time a doctor is genuinely unavailable disappears from the patient's slot
// grid: the server drops every slot an active event overlaps, SILENTLY (a
// patient is never told why a slot is missing, only that it isn't there).
//
// Backend: /api/doctor-calendar-events, tenant-scoped by the model guard. A
// doctor may only touch rows on their own calendar; a tenant admin and staff
// holding the appointment permission may touch the calendar they are looking at.
// None of that is decided here — the UI hides what it should, the API refuses.

// List the events overlapping a visible range: GET ?doctorId&from&to[&timeZone]
export const getCalendarEvents = createAsyncThunk(
    'calendarEvents/getCalendarEvents',
    async (params, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/doctor-calendar-events`, { params });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// "What would a block here hit?" GET /conflicts?doctorId&startUTC&endUTC[&excludeEventId]
// → { hasConflict, appointments: [...], events: [...] }. Asked by the event
// form while the time is being picked; not kept in the store because the
// answer belongs to one form's current fields, nothing else. The write paths
// run the same check server-side and answer 409, so this is a courtesy, not
// the rule.
export const checkCalendarEventConflicts = createAsyncThunk(
    'calendarEvents/checkCalendarEventConflicts',
    async (params, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/doctor-calendar-events/conflicts`, { params });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// Create: { doctorId, title, eventType, notes?, timeZone, startUTC, endUTC }
// (no all-day flag any more — a block is always a from and a to). A clash with
// a live appointment or another event is refused: 409 EVENT_OVERLAPS_APPOINTMENT
// / EVENT_OVERLAPS_EVENT with `conflicts` in the body.
export const createCalendarEvent = createAsyncThunk(
    'calendarEvents/createCalendarEvent',
    async (payload, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/doctor-calendar-events`, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// Update one event. The body is the same shape as create, applied partially.
export const updateCalendarEvent = createAsyncThunk(
    'calendarEvents/updateCalendarEvent',
    async ({ eventId, ...payload }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`${baseURL}/doctor-calendar-events/${eventId}`, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// Hard delete — a cancelled event is simply removed, so there is no dead row
// left behind still blocking slots.
export const deleteCalendarEvent = createAsyncThunk(
    'calendarEvents/deleteCalendarEvent',
    async (eventId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`${baseURL}/doctor-calendar-events/${eventId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

const calendarEventsSlice = createSlice({
    name: 'calendarEvents',
    initialState: {
        events: [],
        loading: false,
        saving: false,
        error: null,
    },
    reducers: {
        clearCalendarEventsError: (state) => {
            state.error = null;
        },
        // Called when the calendar has no doctor to show. Leaving the previous
        // doctor's events on screen would attribute one doctor's theatre list to
        // another.
        clearCalendarEvents: (state) => {
            state.events = [];
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // List
            .addCase(getCalendarEvents.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getCalendarEvents.fulfilled, (state, action) => {
                state.loading = false;
                state.events = Array.isArray(action.payload?.events) ? action.payload.events : [];
            })
            .addCase(getCalendarEvents.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                // An unreachable endpoint must not leave a stale week on screen.
                state.events = [];
            })
            // Create
            .addCase(createCalendarEvent.pending, (state) => {
                state.saving = true;
                state.error = null;
            })
            .addCase(createCalendarEvent.fulfilled, (state) => {
                state.saving = false;
            })
            .addCase(createCalendarEvent.rejected, (state, action) => {
                state.saving = false;
                state.error = action.payload;
            })
            // Update
            .addCase(updateCalendarEvent.pending, (state) => {
                state.saving = true;
                state.error = null;
            })
            .addCase(updateCalendarEvent.fulfilled, (state) => {
                state.saving = false;
            })
            .addCase(updateCalendarEvent.rejected, (state, action) => {
                state.saving = false;
                state.error = action.payload;
            })
            // Delete
            .addCase(deleteCalendarEvent.pending, (state) => {
                state.saving = true;
                state.error = null;
            })
            .addCase(deleteCalendarEvent.fulfilled, (state, action) => {
                state.saving = false;
                const eventId = action.meta.arg;
                state.events = state.events.filter((e) => e.eventId !== eventId);
            })
            .addCase(deleteCalendarEvent.rejected, (state, action) => {
                state.saving = false;
                state.error = action.payload;
            });
    },
});

export const { clearCalendarEventsError, clearCalendarEvents } = calendarEventsSlice.actions;
export default calendarEventsSlice.reducer;
